import { Req, Res } from '@cbo/shared-library';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { CreateUpdateGlMappingRequest, JobCodeMappingId, useAdminRequests } from './index';

/**
 * USER PREFERENCES
 */
export const useUserPreferencesMutation = (): UseMutationResult<
  Res.Admin.UserPreferences,
  unknown,
  { preferences: Req.Admin.UserPreferences },
  unknown
> => {
  const { updateUserPreferences } = useAdminRequests();

  return useMutation(
    (variables: { preferences: Req.Admin.UserPreferences }) => updateUserPreferences(variables.preferences),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * COMPANY LINKS
 */
export const useCompanyLinkUpdateMutation = (): UseMutationResult<
  Res.Firebase.Link,
  unknown,
  { linkToEdit: Req.Firebase.Link },
  unknown
> => {
  const { editCompanyLink } = useAdminRequests();

  return useMutation((variables: { linkToEdit: Req.Firebase.Link }) => editCompanyLink(variables.linkToEdit), {
    useErrorBoundary: false,
  });
};

export const useCompanyLinkCreateMutation = (): UseMutationResult<
  Res.Firebase.Link,
  unknown,
  { newLink: Req.Firebase.LinkData },
  unknown
> => {
  const { addNewCompanyLink } = useAdminRequests();

  return useMutation((variables: { newLink: Req.Firebase.LinkData }) => addNewCompanyLink(variables.newLink), {
    useErrorBoundary: false,
  });
};

export const useCompanyLinkDeleteMutation = (): UseMutationResult<string, unknown, { linkId: string }, unknown> => {
  const { deleteCompanyLink } = useAdminRequests();

  return useMutation((variables: { linkId: string }) => deleteCompanyLink(variables.linkId), {
    useErrorBoundary: false,
  });
};

export const useNotificationSettingsDeleteMutation = (): UseMutationResult<
  { rowsAffected: number },
  unknown,
  { notificationSettingId: string },
  unknown
> => {
  const { deleteNotificationSettingsData } = useAdminRequests();

  return useMutation(
    (variables: { notificationSettingId: string }) => deleteNotificationSettingsData(variables.notificationSettingId),
    {
      useErrorBoundary: false,
    }
  );
};

export const useNotificationSettingsCreateMutation = (): UseMutationResult<
  Res.Notifications.NotificationSettings,
  unknown,
  { newNotificationSetting: Req.Notifications.NotificationSettingsData },
  unknown
> => {
  const { createNotificationSettingsData } = useAdminRequests();

  return useMutation(
    (variables: { newNotificationSetting: Req.Notifications.NotificationSettingsData }) =>
      createNotificationSettingsData(variables.newNotificationSetting),
    {
      useErrorBoundary: false,
    }
  );
};

export const useNotificationSettingsUpdateMutation = (): UseMutationResult<
  Res.Notifications.NotificationSettings,
  unknown,
  { id: string; notificationSetting: Req.Notifications.NotificationSettingsData },
  unknown
> => {
  const { updateNotificationSettingsData } = useAdminRequests();
  return useMutation(
    (variables: { id: string; notificationSetting: Req.Notifications.NotificationSettingsData }) =>
      updateNotificationSettingsData(variables.id, variables.notificationSetting),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * Site Group Management
 */
export const useSiteDayPartsUpdateMutation = (): UseMutationResult<
  Res.SiteManagement.SiteViewData,
  unknown,
  { siteDayParts: Req.SiteManagement.DayPart[]; enterpriseUnitId: string },
  unknown
> => {
  const { updateSiteDayParts } = useAdminRequests();
  return useMutation(
    (variables: { siteDayParts: Req.SiteManagement.DayPart[]; enterpriseUnitId: string }) =>
      updateSiteDayParts(variables.siteDayParts, variables.enterpriseUnitId),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * FISCAL CALENDAR
 */
export const useCreateFiscalCalendarMutation = (): UseMutationResult<
  Res.Calendar.FiscalCalendar,
  unknown,
  { fiscalCalendar: Req.Calendar.FiscalCalendarData },
  unknown
> => {
  const { createFiscalCalendar } = useAdminRequests();

  return useMutation(
    (variables: { fiscalCalendar: Req.Calendar.FiscalCalendarData }) => createFiscalCalendar(variables.fiscalCalendar),
    {
      useErrorBoundary: false,
    }
  );
};

export const useUpdateFiscalCalendarMutation = (): UseMutationResult<
  Res.Calendar.FiscalCalendar,
  unknown,
  { id: string; fiscalCalendar: Req.Calendar.FiscalCalendarData },
  unknown
> => {
  const { updateFiscalCalendar } = useAdminRequests();

  return useMutation(
    (variables: { id: string; fiscalCalendar: Req.Calendar.FiscalCalendarData }) =>
      updateFiscalCalendar(variables.id, variables.fiscalCalendar),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * PAYROLL CALENDAR
 */
export const usePayrollCalendarUpsertMutation = (): UseMutationResult<
  Res.Calendar.PayrollCalendar,
  unknown,
  { payrollCalendar: Req.Calendar.PayrollCalendarData },
  unknown
> => {
  const { upsertPayrollCalendar } = useAdminRequests();

  return useMutation((variables: { payrollCalendar: Req.Calendar.PayrollCalendarData }) =>
    upsertPayrollCalendar(variables.payrollCalendar)
  );
};

/**
 * GL Mapping
 */
export const useUpdateGlMappingMutation = (): UseMutationResult<
  JobCodeMappingId | undefined,
  unknown,
  { id: string; body: CreateUpdateGlMappingRequest },
  unknown
> => {
  const { updateGlMapping } = useAdminRequests();

  return useMutation((variables: { id: string; body: CreateUpdateGlMappingRequest }) =>
    updateGlMapping(variables.id, variables.body)
  );
};

export const useCreateGlMappingMutation = (): UseMutationResult<
  JobCodeMappingId | undefined,
  unknown,
  { body: CreateUpdateGlMappingRequest },
  unknown
> => {
  const { createGlMapping } = useAdminRequests();

  return useMutation((variables: { body: CreateUpdateGlMappingRequest }) => createGlMapping(variables.body));
};

export const useDeleteGlMappingMutation = (): UseMutationResult<
  JobCodeMappingId | undefined,
  unknown,
  { id: string },
  unknown
> => {
  const { deleteGlMapping } = useAdminRequests();

  return useMutation((variables: { id: string }) => deleteGlMapping(variables.id));
};
